import init from './init.js';

const width = 1080;
const height = 1920;
PIXI.settings.TARGET_FPMS = 0.005;

function isInRange(range, target) {
  return target >= range[0] && target < range[1];
}

function getProgressInRange(range) {
  const now = new Date();
  const start = new Date().setHours(range[0], 0, 0);
  const end = new Date().setHours(range[1], 0, 0);
  const progress = (now - start) / (end - start);

  return Math.min(Math.max(progress, 0), 1);
}

function getSoraIndexAndProgress() {
  const now = new Date();
  const currentHour = now.getHours();

  if (isInRange([5, 9], currentHour)) {
    return ['none', 0];
  } else if (isInRange([9, 16], currentHour)) {
    return [0, getProgressInRange([9, 16])];
  } else if (isInRange([16, 18], currentHour)) {
    return [1, getProgressInRange([16, 18])];
  }

  return [2, getProgressInRange(currentHour < 5 ? [-6, 5] : [18, 29])];
}

const manifest = {
  width,
  height,
  bundles: [
    {
      name: 'preload',
      root: 'assets/4/',
      assets: [
        {
          name: 'background',
          srcs: 'background.png',
        },
        {
          name: 'hito',
          srcs: 'hito/hito.json',
        },
        {
          name: 'kago',
          srcs: 'kago/kago.json',
        },
        { name: 'empty', srcs: 'empty.png' },
        { name: 'kemuri', srcs: 'kemuri/kemuri.json' },
        {
          name: 'hana',
          srcs: 'hana/hana.json',
        },
        {
          name: 'sora',
          srcs: 'sora/sora.json',
        },
      ],
    },
    {
      name: 'background',
      root: 'assets/4/',
      assets: [
        {
          name: 'pad',
          srcs: 'pad/pad.json',
        },
        {
          name: 'kumo',
          srcs: 'kumo/kumo.png',
        },
      ],
    },
    {
      name: 'final',
      root: 'assets/4/',
      assets: [
        {
          name: 'tori',
          srcs: 'tori/tori.json',
        },
        {
          name: 'neko',
          frames: 2,
          srcs: 'neko/4-neko-*.png',
        },
      ],
    },
  ],
};

async function main() {
  const { app, getAssetOfBundle, Sprite, AnimatedSprite } = await init(
    manifest
  );

  const preloadAssets = await PIXI.Assets.loadBundle('preload');
  PIXI.Assets.backgroundLoadBundle('background');

  const getPreloadAsset = getAssetOfBundle('preload', preloadAssets);
  const background = new Sprite(getPreloadAsset('background'));

  const hito = new AnimatedSprite(getPreloadAsset('hito').animations['7-hito']);
  hito.interactive = true;
  hito.hitArea = new PIXI.Polygon(
    624,
    822,
    555,
    935,
    636,
    1131,
    591,
    1344,
    404,
    1395,
    300,
    1556,
    396,
    1601,
    549,
    1803,
    1033,
    1641,
    933,
    1340,
    762,
    1104,
    797,
    920,
    720,
    823
  );
  hito.on('pointerdown', () => {
    hito.gotoAndStop((hito.currentFrame + 1) % 2);
  });

  const hana = new AnimatedSprite(getPreloadAsset('hana').animations['6-hana']);

  const kago = new AnimatedSprite(getPreloadAsset('kago').animations['4-kago']);

  kago.interactive = true;
  kago.hitArea = new PIXI.Rectangle(811, 1005, 216, 277);

  const kemuri = new AnimatedSprite([
    getPreloadAsset('empty'),
    ...getPreloadAsset('kemuri').animations['3-kemuri'],
  ]);
  kemuri.animationSpeed = 0.2;
  kemuri.loop = true;
  kemuri.play();

  const sora = new AnimatedSprite(getPreloadAsset('sora').animations['sora']);

  const updateSora = () => {
    const [index, progress] = getSoraIndexAndProgress();
    if (index === 'none') {
      sora.visible = false;
    } else {
      sora.gotoAndStop(index);
      sora.alpha = 1 - Math.abs(0.5 - progress) / 0.5;
    }
  };

  updateSora();
  setInterval(() => {
    updateSora();
  }, 60 * 1000); // 60 seconds

  app.stage.addChild(background, sora, kago, kemuri, hito, hana);

  const backgroundAssets = await PIXI.Assets.loadBundle('background');
  const getBackgroundAsset = getAssetOfBundle('background', backgroundAssets);

  const kumoTop = new Sprite(getBackgroundAsset('kumo'));
  kumoTop.width = 1352;
  kumoTop.height = 261;

  const kumoBottom = new Sprite(getBackgroundAsset('kumo'));
  kumoBottom.width = 1352;
  kumoBottom.height = 261;
  kumoBottom.scale.x = -1;
  kumoBottom.scale.y = -1;
  kumoBottom.x = width;
  kumoBottom.y = height;

  let counter = 0;
  app.ticker.add((delta) => {
    counter += delta;
    kumoTop.x = -(Math.cos(counter / 20) + 1) * 130;
    kumoBottom.x = width + (Math.cos(counter / 20) + 1) * 130;
  });

  const pad = new AnimatedSprite(getBackgroundAsset('pad').animations['2-pad']);
  pad.loop = false;
  pad.animationSpeed = 0.1;
  pad.interactive = true;
  pad.hitArea = new PIXI.Polygon(
    390,
    1277,
    432,
    1384,
    588,
    1323,
    610,
    1265,
    589,
    1224
  );
  pad.gotoAndStop(5);
  pad.alpha = 0;

  pad.onComplete = () => {
    setTimeout(() => {
      pad.fadeOut(3000);
      setTimeout(() => {
        pad.gotoAndStop(5);
      }, 2800);
    }, 2000);
  };

  pad.on('pointerdown', () => {
    pad.fadeIn(2000);
    setTimeout(() => {
      pad.gotoAndPlay(5);
    }, 1000);
  });

  app.stage.addChild(pad);

  const finalAssets = await PIXI.Assets.loadBundle('final');
  const getFinalAsset = getAssetOfBundle('final', finalAssets);

  const tori = new AnimatedSprite(getFinalAsset('tori').animations['tori']);
  tori.width = 40;
  tori.height = 40;
  tori.x = -40;
  tori.y = 344;
  tori.loop = true;
  tori.play();

  const toriOnTick = (t) => {
    const target = tori.x + 19 * t;
    tori.x = target > width ? target - width - 40 : target;

    if (target > width / 8 && target < width) {
      hana.gotoAndStop(1);
    }

    if (target > width) {
      kago.gotoAndStop(0);
      hana.gotoAndStop(0);
      app.ticker.remove(toriOnTick);
    }
  };

  kago.on('pointerdown', () => {
    if (kago.currentFrame === 0) {
      kago.gotoAndStop(1);
      app.ticker.add(toriOnTick);
    }
  });

  const neko = new AnimatedSprite(getFinalAsset('neko'));
  neko.interactive = true;
  neko.hitArea = new PIXI.Circle(300, 1700, 137);
  neko.on('pointerdown', () => {
    neko.gotoAndStop((neko.currentFrame + 1) % 2);
  });

  app.stage.addChild(neko, kumoTop, kumoBottom);
  app.stage.addChildAt(tori, 1);
}

main();
